import { MeshBVH } from "three-mesh-bvh";

AFRAME.utils.device.isMobile();

class CapMeshLambertMaterial extends THREE.MeshLambertMaterial {
    static fromMeshStandardMaterial(meshStandardMaterial) {
        const capMeshLambertMaterial = new CapMeshLambertMaterial();
        THREE.Material.prototype.copy.call(
            capMeshLambertMaterial,
            meshStandardMaterial
        );
        capMeshLambertMaterial.color.copy(meshStandardMaterial.color);
        capMeshLambertMaterial.map = meshStandardMaterial.map;
        capMeshLambertMaterial.envMap = meshStandardMaterial.envMap;
        capMeshLambertMaterial.lightMap = meshStandardMaterial.lightMap;
        capMeshLambertMaterial.lightMapIntensity =
            meshStandardMaterial.lightMapIntensity;
        capMeshLambertMaterial.aoMap = meshStandardMaterial.aoMap;
        capMeshLambertMaterial.aoMapIntensity =
            meshStandardMaterial.aoMapIntensity;
        capMeshLambertMaterial.emissive.copy(meshStandardMaterial.emissive);
        capMeshLambertMaterial.emissiveMap = meshStandardMaterial.emissiveMap;
        capMeshLambertMaterial.emissiveIntensity =
            meshStandardMaterial.emissiveIntensity;
        capMeshLambertMaterial.normalMapType =
            meshStandardMaterial.normalMapType;
        capMeshLambertMaterial.normalMap = meshStandardMaterial.normalMap;
        capMeshLambertMaterial.normalScale.copy(
            meshStandardMaterial.normalScale
        );
        capMeshLambertMaterial.bumpMap = meshStandardMaterial.bumpMap;
        capMeshLambertMaterial.bumpScale = meshStandardMaterial.bumpScale;
        capMeshLambertMaterial.displacementMap =
            meshStandardMaterial.displacementMap;
        capMeshLambertMaterial.displacementScale =
            meshStandardMaterial.displacementScale;
        capMeshLambertMaterial.displacementBias =
            meshStandardMaterial.displacementBias;
        capMeshLambertMaterial.alphaMap = meshStandardMaterial.alphaMap;
        capMeshLambertMaterial.reflectivity = 0.5;
        capMeshLambertMaterial.refractionRatio =
            meshStandardMaterial.refractionRatio;
        capMeshLambertMaterial.wireframe = meshStandardMaterial.wireframe;
        capMeshLambertMaterial.wireframeLinewidth =
            meshStandardMaterial.wireframeLinewidth;
        capMeshLambertMaterial.wireframeLinecap =
            meshStandardMaterial.wireframeLinecap;
        capMeshLambertMaterial.wireframeLinejoin =
            meshStandardMaterial.wireframeLinejoin;
        capMeshLambertMaterial.skinning = meshStandardMaterial.skinning;
        capMeshLambertMaterial.morphTargets = meshStandardMaterial.morphTargets;
        capMeshLambertMaterial.morphNormals = meshStandardMaterial.morphNormals;
        return capMeshLambertMaterial;
    }
}

class CapMeshPhongMaterial extends THREE.MeshPhongMaterial {
    static fromMeshStandardMaterial(standardMaterial) {
        const capMeshPhongMaterial = new CapMeshPhongMaterial();

        THREE.Material.prototype.copy.call(
            capMeshPhongMaterial,
            standardMaterial
        );

        capMeshPhongMaterial.color.copy(standardMaterial.color);
        capMeshPhongMaterial.map = standardMaterial.map;
        capMeshPhongMaterial.lightMap = standardMaterial.lightMap;
        capMeshPhongMaterial.lightMapIntensity =
            standardMaterial.lightMapIntensity;
        capMeshPhongMaterial.aoMap = standardMaterial.aoMap;
        capMeshPhongMaterial.aoMapIntensity = standardMaterial.aoMapIntensity;
        capMeshPhongMaterial.emissive.copy(standardMaterial.emissive);
        capMeshPhongMaterial.emissiveMap = standardMaterial.emissiveMap;
        capMeshPhongMaterial.emissiveIntensity =
            standardMaterial.emissiveIntensity;
        capMeshPhongMaterial.normalMapType = standardMaterial.normalMapType;
        capMeshPhongMaterial.normalMap = standardMaterial.normalMap;
        capMeshPhongMaterial.normalScale.copy(standardMaterial.normalScale);
        capMeshPhongMaterial.bumpMap = standardMaterial.bumpMap;
        capMeshPhongMaterial.bumpScale = standardMaterial.bumpScale;
        capMeshPhongMaterial.displacementMap = standardMaterial.displacementMap;
        capMeshPhongMaterial.displacementScale =
            standardMaterial.displacementScale;
        capMeshPhongMaterial.displacementBias =
            standardMaterial.displacementBias;
        capMeshPhongMaterial.alphaMap = standardMaterial.alphaMap;
        capMeshPhongMaterial.wireframe = standardMaterial.wireframe;
        capMeshPhongMaterial.wireframeLinewidth =
            standardMaterial.wireframeLinewidth;
        capMeshPhongMaterial.wireframeLinecap =
            standardMaterial.wireframeLinecap;
        capMeshPhongMaterial.wireframeLinejoin =
            standardMaterial.wireframeLinejoin;
        capMeshPhongMaterial.skinning = standardMaterial.skinning;
        capMeshPhongMaterial.morphTargets = standardMaterial.morphTargets;
        capMeshPhongMaterial.morphNormals = standardMaterial.morphNormals;

        return capMeshPhongMaterial;
    }
}

function getMeshMaterial(material, materialType) {
    if (material.isMeshBasicMaterial) {
        return material;
    } else if (materialType === "medium") {
        return CapMeshPhongMaterial.fromMeshStandardMaterial(material);
    } else if (materialType === "low") {
        return CapMeshLambertMaterial.fromMeshStandardMaterial(material);
    } else {
        return material;
    }
}

function applyMaterial(node, n) {
    if (node.material) {
        if (Array.isArray(node.material)) {
            node.material = node.material.map(n);
        } else {
            node.material = n(node.material);
        }
    }
}

const GLTFCache = {};

function parallelTraverse(a, b, callback) {
    callback(a, b);

    for (let i = 0; i < a.children.length; i++) {
        parallelTraverse(a.children[i], b.children[i], callback);
    }
}

function cloneSkinnedMesh(source, n) {
    const cloneLookup = new Map();
    const s = new Map();
    const clone = source.clone();

    parallelTraverse(source, clone, function (sourceNode, clonedNode) {
        cloneLookup.set(sourceNode, clonedNode);
    });

    source.traverse((sourceMesh) => {
        const node = cloneLookup.get(sourceMesh);

        if (n) {
            node.uuid = sourceMesh.uuid;
        }
        s.set(sourceMesh.uuid, node.uuid);
    });

    source.traverse((node) => {
        const newNode = nodeMap.get(node);

        if (!newNode) return;

        if (node.material) {
            newNode.material = node.material.clone();
        }

        if (node.animations) {
            newNode.animations = node.animations.map((animation) => {
                const tracks = animation.tracks.map((track) => {
                    const {
                        nodeName,
                        objectName,
                        objectIndex,
                        propertyName,
                        propertyIndex,
                    } = THREE.PropertyBinding.parseTrackName(track.name);

                    let name = "";

                    if (nodeName !== undefined) {
                        name += nodeMap.get(nodeName);
                    }
                    if (objectName !== undefined) {
                        name += "." + objectName;
                    }
                    if (objectIndex !== undefined) {
                        name += "[" + objectIndex + "]";
                    }
                    if (propertyName !== undefined) {
                        name += "." + propertyName;
                    }
                    if (propertyIndex !== undefined) {
                        name += "[" + propertyIndex + "]";
                    }

                    const clonedTrack = track.clone();
                    clonedTrack.name = name;

                    return clonedTrack;
                });

                return new THREE.AnimationClip(
                    animation.name,
                    animation.duration,
                    tracks
                );
            });
        }

        if (node.isMesh && node.geometry.boundsTree) {
            newNode.geometry.boundsTree = node.geometry.boundsTree;
        }

        if (!node.isSkinnedMesh) {
            return;
        }

        const bones = node.skeleton.bones;

        newNode.skeleton = node.skeleton.clone();
        newNode.skeleton.bones = bones.map((bone) => {
            if (!nodeMap.has(bone)) {
                throw new Error(
                    "Required bones are not descendants of the given object."
                );
            }

            return nodeMap.get(bone);
        });

        newNode.bind(newNode.skeleton, node.bindMatrix);
    });
}

function cloneGltf(gltf) {
    if (!gltf) return;
    return {
        animations: gltf.animations,
        scene: cloneSkinnedMesh(gltf.scene),
    };
}

document.addEventListener("DOMContentLoaded", () => {
    AFRAME.registerComponent("gltf-model-plus", {
        schema: {
            src: {
                type: "string",
            },
            contentType: {
                type: "string",
            },
            useCache: {
                default: true,
            },
            updateMatrix: {
                default: false,
            },
            envMap: {
                default: false,
            },
            inflate: {
                default: false,
            },
            modelToWorldScale: {
                type: "number",
                default: 1,
            },
            useStandard: {
                default: false,
            },
            startAnimationClip: {
                type: "string",
                default: "MAIN",
            },
            setAnimations: {
                default: true,
            },
            setMatrixAutoUpdate: {
                default: false,
            },
            forceMatrixAutoUpdate: {
                default: false,
            },
        },
        init() {
            this.selfVr = this.data.selfVr;
            this.preferredTechnique =
                window.APP && "low" === window.APP.quality
                    ? "KHR_materials_unlit"
                    : "pbrMetallicRoughness";
            const gltfSystem = this.el.sceneEl.systems["gltf-system"];
            this.gltfLoader = gltfSystem.getGLTFLoader();
        },
        update(t) {
            var data = this.data;
            this.el, performance.now();
            this.applySrc(
                data.src,
                this.data.contentType,
                this.data.updateMatrix
            );
        },
        loadTemplates() {
            this.templates = {};

            this.el
                .querySelectorAll(":scope > template")
                .forEach((template) => {
                    document.importNode(
                        template.firstElementChild ||
                            template.content.firstElementChild,
                        true
                    );
                });
        },
        async loadModel(src, contentType, technique, useCache) {
            if (useCache) {
                if (!GLTFCache[src]) {
                    GLTFCache[src] = await this.gltfLoader.loadAsync(src);
                }

                return cloneGltf(GLTFCache[src]);
            } else {
                return await this.gltfLoader.loadAsync(src);
            }
        },
        async applySrc(src, name, updateMatrix) {
            let parser;
            let scene, animations;

            try {
                if (src && src.charAt(0) === "#") {
                    const node = document.getElementById(src.substring(1));
                    src = node.getAttribute("src");
                }

                if (src === this.lastSrc) {
                    return;
                }

                this.lastSrc = src;

                if (!src) {
                    if (this.inflatedEl) {
                        this.removeInflatedEl();
                    }
                    return;
                }

                const result = await this.loadModel(
                    src,
                    name,
                    this.preferredTechnique,
                    this.data.useCache,
                    this.data.useStandard
                );

                if (!result) {
                    return;
                }

                parser = result.parser;

                if (parser) {
                    parser.cache.removeAll();
                    parser.textureCache = {};
                    parser.primitiveCache = {};
                    parser.meshCache = {
                        refs: {},
                        uses: {},
                    };
                    parser.cameraCache = {
                        refs: {},
                        uses: {},
                    };
                    parser.lightCache = {
                        refs: {},
                        uses: {},
                    };
                    parser.sourceCache = {};
                    parser.json = {};
                    parser.associations.clear();
                }

                if (src !== this.lastSrc) {
                    return;
                }

                scene = result.scene || result.scenes[0];
                animations = result.animations;

                if (animations.length > 0 && this.data.setAnimations) {
                    this.el.setAttribute("animation-mixer", animations);
                    this.el.components["animation-mixer"].initMixer(animations);
                }

                scene.traverse((node) => {
                    const isMesh =
                        node.isMesh && node.geometry.isBufferGeometry;
                    const hasBoundsTree = isMesh && node.geometry.boundsTree;

                    if (
                        isMesh &&
                        !hasBoundsTree &&
                        node.geometry.attributes.position
                    ) {
                        const positions = node.geometry.attributes.position;

                        if (positions.count !== null) {
                            const positionArray = new Float32Array(
                                3 * positions.count
                            );

                            for (let i = 0; i < positions.count; i++) {
                                positionArray[3 * i + 0] =
                                    positions.getX(i) * scene.scale.x +
                                    scene.position.x;
                                positionArray[3 * i + 1] =
                                    positions.getY(i) * scene.scale.y +
                                    scene.position.y;
                                positionArray[3 * i + 2] =
                                    positions.getZ(i) * scene.scale.z +
                                    scene.position.z;
                            }
                            const newGeometry = new THREE.BufferGeometry();
                            newGeometry.setAttribute(
                                "position",
                                new THREE.BufferAttribute(positionArray, 3)
                            );
                            newGeometry.setIndex(node.geometry.getIndex());
                            node.geometry.boundsTree = new MeshBVH(
                                newGeometry,
                                {
                                    strategy: 0,
                                    maxDepth: 30,
                                }
                            );
                            newGeometry.dispose();
                        }
                    }

                    // if (
                    //     (animations.length > 0 && this.data.setAnimations) ||
                    //     this.data.forceMatrixAutoUpdate
                    // ) {
                    //     node.matrixAutoUpdate =
                    //         THREE.Object3D.DEFAULT_MATRIX_AUTO_UPDATE;
                    // } else {
                    //     node.matrixAutoUpdate = false;
                    //     node.updateMatrixWorld();
                    // }

                    // applyMaterial(node, (material) =>
                    //     getMeshMaterial(material, "medium")
                    // );

                    // if (node.isMesh) {
                    //     const materials = Array.isArray(node.material)
                    //         ? node.material
                    //         : [node.material];
                    //     for (const material of materials) {
                    //         for (const key in material) {
                    //             const texture = material[key];
                    //             if (texture && texture.isTexture) {
                    //                 texture.onUpdate = function () {
                    //                     texture.mipmaps.length = 0;
                    //                     if (texture.image.close) {
                    //                         texture.image.close();
                    //                     }
                    //                 };
                    //             }
                    //         }
                    //     }
                    // }
                });

                this.el.setObject3D("Mesh", scene);
                this.el.object3D.matrixWorldNeedsUpdate = !!updateMatrix;

                if (this.data.setMatrixAutoUpdate) {
                    this.el.object3D.matrixAutoUpdate = true;
                }

                this.el.object3D.updateMatrixWorld(true);
                this.el.object3D.updateMatrix(true);
                this.el.emit("model-loaded", {
                    format: "gltf",
                    model: scene,
                });
            } catch (error) {
                console.error(error);

                delete GLTFCache[src];
                this.el.emit("model-error", {
                    format: "gltf",
                    src: src,
                });
            }
        },
        disposeLastInflatedEl() {
            if (this.inflatedEl) {
                this.inflatedEl.parentNode.removeChild(this.inflatedEl);

                this.inflatedEl.object3D.traverse((object3D) => {
                    if (object3D.material && object3D.material.dispose) {
                        object3D.material.dispose();
                    }

                    if (object3D.geometry) {
                        object3D.geometry.dispose &&
                            object3D.geometry.dispose();
                        object3D.geometry.boundsTree = null;
                    }
                });
                delete this.inflatedEl;
                return this.el.removeAttribute("animation-mixer");
            }
        },
        remove() {
            this.disposeLastInflatedEl();
            this.el.removeObject3D("Mesh");
            this.el.removeAttribute("animation-mixer");
        },
    });
});
